import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { ArrowLeft, PencilSimple, FloppyDisk, X, Sparkle, Trash, CheckCircle, ShareNetwork, Copy, Globe, GlobeX, MapPin, EyeSlash, Eye, FilePdf, Star, Plus } from "@phosphor-icons/react";
import LocationPicker from "../components/LocationPicker";
import WorldMap from "../components/WorldMap";

const FIELDS = [
  ["nome", "Nome"],
  ["categoria_dettaglio", "Sotto-categoria (libera)"],
  ["periodo", "Periodo"],
  ["datazione", "Datazione"],
  ["materiale", "Materiale"],
  ["cultura", "Cultura"],
  ["dimensioni_stimate", "Dimensioni"],
  ["stato_conservazione", "Stato di conservazione"],
  ["rarita", "Rarità"],
];

const CATEGORIES = ["Moneta o Medaglia", "Devozionale", "Bottone", "Anello", "Militaria", "Vario"];

const ReadField = ({ label, value }) => (
  <div className="border-b border-black/5 py-3">
    <div className="label-eyebrow mb-1">{label}</div>
    <div className="text-[#1E2520]">{value || <span className="text-[#5C635A] italic">non specificato</span>}</div>
  </div>
);

export default function ArtifactDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isNew = params.get("new") === "1";
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [directives, setDirectives] = useState("");
  const [reidentifying, setReidentifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // When user changes category in edit mode, allow assigning a new inventory code.
  const [reassignCode, setReassignCode] = useState(false);
  const [nextCodePreview, setNextCodePreview] = useState("");
  // Photo management — drives the add/delete/set-main flows in edit mode.
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  // Per-user preference for the photo input UX: 'file' (single button, system
  // chooser on mobile) or 'file_and_camera' (separate "Scatta foto" button
  // that opens the device camera directly via capture="environment").
  const [photoInputMode, setPhotoInputMode] = useState("file");
  const MAX_PHOTOS = 4;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setPhotoInputMode(data?.photo_input_mode || "file");
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/artifacts/${id}`);
      setItem(data);
      setDraft(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Original category at edit-start, used to detect a change.
  const originalCategoria = item?.identification?.categoria || "";
  const currentCategoria = draft?.identification?.categoria || "";
  const categoriaChanged = editing && currentCategoria && currentCategoria !== originalCategoria;

  // Fetch preview of the next code in the new category when needed.
  useEffect(() => {
    if (!categoriaChanged || !reassignCode) { setNextCodePreview(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/inventory/next-code", { params: { categoria: currentCategoria } });
        if (!cancelled) setNextCodePreview(data?.next_code || "");
      } catch (e) {
        if (!cancelled) setNextCodePreview("");
      }
    })();
    return () => { cancelled = true; };
  }, [categoriaChanged, reassignCode, currentCategoria]);

  // Reset the reassign flag if the user reverts the category.
  useEffect(() => {
    if (!categoriaChanged && reassignCode) setReassignCode(false);
  }, [categoriaChanged, reassignCode]);

  const setIdent = (k, v) =>
    setDraft((d) => ({ ...d, identification: { ...d.identification, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        identification: draft.identification,
        user_notes: draft.user_notes || "",
        inventory_code: draft.inventory_code || "",
        location: draft.location || null,
        location_visible_in_share: !!draft.location_visible_in_share,
        reassign_inventory_code: reassignCode,
      };
      const { data } = await api.patch(`/artifacts/${id}`, body);
      setItem(data); setDraft(data); setEditing(false);
      setReassignCode(false); setNextCodePreview("");
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const reidentify = async () => {
    setReidentifying(true);
    try {
      const { data } = await api.post(`/artifacts/${id}/reidentify`, { directives });
      setItem(data); setDraft(data); setDirectives("");
    } catch (e) {
      console.error(e);
    } finally {
      setReidentifying(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questo reperto?")) return;
    await api.delete(`/artifacts/${id}`);
    navigate("/");
  };

  // --- Photo management handlers ---
  // All three sync the local `item` and `draft` state with the backend
  // response so the UI updates immediately and the reidentification flow
  // sees the new photo list as soon as it's persisted.
  const addPhotos = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const current = item?.photos?.length || 0;
    const remaining = MAX_PHOTOS - current;
    if (remaining <= 0) {
      alert(`Hai già ${MAX_PHOTOS} foto: rimuovi prima una foto per aggiungerne una nuova.`);
      return;
    }
    const toUpload = list.slice(0, remaining);
    if (list.length > remaining) {
      alert(`Carico solo ${remaining} foto su ${list.length} (limite ${MAX_PHOTOS} totali).`);
    }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      toUpload.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/artifacts/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItem(data);
      setDraft(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore nel caricamento delle foto");
    } finally {
      setPhotoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deletePhoto = async (index) => {
    if (!window.confirm("Eliminare questa foto dal catalogo? L'azione è definitiva.")) return;
    setPhotoBusy(true);
    try {
      const { data } = await api.delete(`/artifacts/${id}/photos/${index}`);
      setItem(data);
      setDraft(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore nell'eliminazione della foto");
    } finally {
      setPhotoBusy(false);
    }
  };

  const setMainPhoto = async (index) => {
    if (index === 0) return; // already main
    setPhotoBusy(true);
    try {
      const { data } = await api.post(`/artifacts/${id}/photos/main`, { index });
      setItem(data);
      setDraft(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore nell'impostare la foto principale");
    } finally {
      setPhotoBusy(false);
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/artifacts/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reperto_${item?.inventory_code || id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF export failed", e);
      // When responseType is 'blob', error responses are also blobs — we have
      // to decode the JSON manually to surface the backend's error message.
      let serverMsg = "";
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          try {
            const obj = JSON.parse(text);
            serverMsg = typeof obj.detail === "string" ? obj.detail : "";
          } catch { serverMsg = text.slice(0, 200); }
        } catch { /* unreadable blob */ }
      } else if (typeof data?.detail === "string") {
        serverMsg = data.detail;
      }
      alert(serverMsg || "Errore nella generazione del PDF. Riprova.");
    }
  };

  const toggleShare = async () => {
    if (item.is_public) {
      await api.delete(`/artifacts/${id}/share`);
      setItem((it) => ({ ...it, is_public: false }));
    } else {
      const { data } = await api.post(`/artifacts/${id}/share`);
      setItem((it) => ({ ...it, is_public: true, share_token: data.share_token }));
    }
  };

  const shareUrl = item && item.share_token
    ? `${window.location.origin}/share/${item.share_token}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.error(e); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: item.identification?.nome || "Reperto",
          text: `Guarda questo reperto: ${item.identification?.nome || ""}`,
          url: shareUrl,
        });
      } catch (e) { /* user cancelled share dialog */ }
    } else {
      copyLink();
    }
  };

  if (loading || !item) {
    return (
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-14">
        <div className="aspect-video scanning rounded-md" />
        <div className="h-10 w-2/3 scanning mt-6" />
      </div>
    );
  }

  const ident = item.identification || {};
  const draftIdent = draft.identification || {};

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12 fade-in">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-[#5C635A] hover:text-[#9C5B42] mb-6"
        data-testid="back-to-catalog"
      >
        <ArrowLeft size={16} /> Torna al catalogo
      </Link>

      {isNew && (
        <div className="mb-6 border border-[#9C5B42]/30 bg-[#F0EBE1] px-4 py-3 flex items-start gap-3 rounded-sm">
          <CheckCircle weight="duotone" size={22} className="text-[#9C5B42] mt-0.5" />
          <div>
            <div className="font-serif text-xl text-[#1E2520]">Reperto aggiunto al catalogo</div>
            <p className="text-sm text-[#5C635A]">
              Controlla la scheda. Se qualcosa non è corretto, usa <em>Correggi</em> oppure
              fornisci suggerimenti all'AI per rianalizzare.
            </p>
          </div>
        </div>
      )}

      {/* Photos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {(item.photos || []).map((p, i) => (
          <div
            key={p}
            className="aspect-square overflow-hidden bg-[#E5E0D8] border border-black/5 group relative"
          >
            <button
              type="button"
              onClick={() => setLightbox(p)}
              className="absolute inset-0 w-full h-full"
              data-testid={`photo-thumb-${i}`}
            >
              <img
                src={fileUrl(p)}
                alt={`Foto ${i + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </button>

            {/* Cover badge — visible always for clarity, not just in edit mode */}
            {i === 0 && (
              <div
                className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#9C5B42] text-white text-[10px] font-mono tracking-wider uppercase px-1.5 py-0.5 rounded-sm pointer-events-none"
                data-testid={`photo-cover-badge-${i}`}
              >
                <Star weight="fill" size={11} /> Copertina
              </div>
            )}

            {/* Edit-mode overlay controls. Stop propagation so the action
                buttons don't double-trigger the lightbox underneath. */}
            {editing && (
              <div className="absolute top-2 right-2 flex flex-col gap-1.5 z-10">
                {i !== 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setMainPhoto(i); }}
                    disabled={photoBusy}
                    title="Imposta come copertina"
                    className="bg-white/95 hover:bg-white text-[#9C5B42] p-1.5 rounded-sm shadow-sm border border-black/5 disabled:opacity-50"
                    data-testid={`photo-set-main-${i}`}
                  >
                    <Star size={16} />
                  </button>
                )}
                {(item.photos || []).length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deletePhoto(i); }}
                    disabled={photoBusy}
                    title="Elimina foto"
                    className="bg-white/95 hover:bg-red-50 text-red-700 p-1.5 rounded-sm shadow-sm border border-red-200 disabled:opacity-50"
                    data-testid={`photo-delete-${i}`}
                  >
                    <Trash size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Add-photo tile — only in edit mode, only while under the cap.
            Three modes:
              - file:            single "Carica file" button
              - camera:          single "Scatta foto" button
              - file_and_camera: two buttons (file + camera) */}
        {editing && (item.photos || []).length < MAX_PHOTOS && (
          <div
            className="aspect-square border-2 border-dashed border-[#9C5B42]/40 bg-[#FFF6EE]/40 flex flex-col items-center justify-center gap-2 text-[#9C5B42] p-3"
            data-testid="add-photo-tile"
          >
            <Plus weight="duotone" size={28} />
            <div className="font-serif text-base text-center leading-tight">
              Aggiungi foto
            </div>
            <div className="flex flex-col gap-1.5 w-full mt-1">
              {(photoInputMode === "file" || photoInputMode === "file_and_camera") && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoBusy}
                  className="text-xs px-2 py-1.5 border border-[#9C5B42]/40 hover:bg-[#9C5B42]/10 transition-colors disabled:opacity-50"
                  data-testid="add-photo-file-btn"
                >
                  {photoBusy ? "Caricamento..." : "Carica file"}
                </button>
              )}
              {(photoInputMode === "camera" || photoInputMode === "file_and_camera") && (
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={photoBusy}
                  className="text-xs px-2 py-1.5 border border-[#9C5B42]/40 hover:bg-[#9C5B42]/10 transition-colors disabled:opacity-50"
                  data-testid="add-photo-camera-btn"
                >
                  Scatta foto
                </button>
              )}
            </div>
            <div className="text-[10px] text-[#5C635A]">
              {(item.photos || []).length}/{MAX_PHOTOS} · max 4
            </div>
          </div>
        )}
      </div>

      {/* Hidden file inputs. Only the inputs relevant to the current mode
          are rendered, to avoid stray click triggers on iOS Safari. */}
      {(photoInputMode === "file" || photoInputMode === "file_and_camera") && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => addPhotos(e.target.files)}
          data-testid="photo-file-input"
        />
      )}
      {(photoInputMode === "camera" || photoInputMode === "file_and_camera") && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => addPhotos(e.target.files)}
          data-testid="photo-camera-input"
        />
      )}

      {/* Header / value */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-3">
            {item.inventory_code && (
              <span
                data-testid="inventory-code-badge"
                className="inline-block bg-[#1E2520] text-[#F9F8F6] font-mono text-sm tracking-[0.18em] px-3 py-1.5 rounded-sm"
              >
                {item.inventory_code}
              </span>
            )}
            <div className="label-eyebrow">{ident.categoria || "Reperto"}{ident.periodo ? ` · ${ident.periodo}` : ""}</div>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#1E2520]">
            {ident.nome || "Reperto senza nome"}
          </h1>
          {ident.categoria_dettaglio && ident.categoria_dettaglio !== ident.categoria && (
            <div className="mt-1 text-sm text-[#5C635A] italic">{ident.categoria_dettaglio}</div>
          )}
          {ident.confidenza && (
            <div className="mt-2 text-xs text-[#5C635A]">
              Confidenza AI: <span className="capitalize">{ident.confidenza}</span>
            </div>
          )}
        </div>
        <div className="card p-5 md:p-6 md:min-w-[260px]">
          <div className="label-eyebrow mb-1">Valore stimato</div>
          <div className="font-serif text-4xl text-[#9C5B42] leading-none">
            € {(ident.valore_stimato_eur ?? 0).toLocaleString("it-IT")}
          </div>
          {(ident.valore_min_eur != null && ident.valore_max_eur != null) && (
            <div className="text-xs text-[#5C635A] mt-2">
              Range: € {ident.valore_min_eur?.toLocaleString("it-IT")} – € {ident.valore_max_eur?.toLocaleString("it-IT")}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        {!editing ? (
          <button onClick={() => setEditing(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="edit-btn">
            <PencilSimple weight="duotone" size={18} /> Correggi
          </button>
        ) : (
          <>
            <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-btn">
              <FloppyDisk weight="duotone" size={18} /> {saving ? "Salvataggio..." : "Salva modifiche"}
            </button>
            <button onClick={() => { setDraft(item); setEditing(false); }} className="btn-ghost inline-flex items-center gap-2" data-testid="cancel-edit-btn">
              <X size={18} /> Annulla
            </button>
          </>
        )}
        <button onClick={remove} className="btn-ghost inline-flex items-center gap-2 border-red-300 text-red-700 hover:bg-red-50" data-testid="delete-btn">
          <Trash weight="duotone" size={18} /> Elimina
        </button>
        <button
          onClick={() => setShareOpen(true)}
          className="btn-ghost inline-flex items-center gap-2"
          data-testid="share-btn"
        >
          <ShareNetwork weight="duotone" size={18} /> Condividi
        </button>
        <button
          onClick={exportPdf}
          className="btn-ghost inline-flex items-center gap-2"
          data-testid="export-pdf-btn"
        >
          <FilePdf weight="duotone" size={18} /> Esporta PDF
        </button>
      </div>

      {/* Identification grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mb-10">
        {/* Inventory code */}
        <div className="border-b border-black/5 py-3">
          <div className="label-eyebrow mb-1">Codice oggetto</div>
          {editing ? (
            <input
              className="input-base font-mono"
              value={draft.inventory_code || ""}
              onChange={(e) => setDraft((d) => ({ ...d, inventory_code: e.target.value.toUpperCase() }))}
              data-testid="edit-inventory-code"
              placeholder="es: M3"
            />
          ) : (
            <div className="font-mono text-[#1E2520]">{item.inventory_code || <span className="text-[#5C635A] italic">non assegnato</span>}</div>
          )}
        </div>
        {/* Categoria principale */}
        <div className="border-b border-black/5 py-3">
          <div className="label-eyebrow mb-1">Categoria principale</div>
          {editing ? (
            <>
              <select
                className="input-base"
                value={draftIdent.categoria || "Vario"}
                onChange={(e) => setIdent("categoria", e.target.value)}
                data-testid="edit-categoria"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {categoriaChanged && (
                <div
                  className="mt-3 p-3 rounded-sm border border-[#9C5B42]/30 bg-[#F0EBE1]/60"
                  data-testid="reassign-code-box"
                >
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reassignCode}
                      onChange={(e) => setReassignCode(e.target.checked)}
                      className="mt-1"
                      data-testid="reassign-code-checkbox"
                    />
                    <span className="text-sm text-[#1E2520]">
                      Aggiorna anche il codice inventariale (<strong className="font-mono">{item?.inventory_code || "—"}</strong>
                      {reassignCode && nextCodePreview && (
                        <> → <strong className="font-mono text-[#9C5B42]" data-testid="next-code-preview">{nextCodePreview}</strong></>
                      )})
                      <span className="block text-xs text-[#5C635A] mt-1">
                        Verrà assegnato il prossimo numero libero nella nuova categoria.
                        Gli altri reperti già catalogati <strong>non</strong> verranno toccati.
                        Ricorda di aggiornare la bustina fisica con il nuovo codice.
                      </span>
                    </span>
                  </label>
                </div>
              )}
            </>
          ) : (
            <div className="text-[#1E2520]">{ident.categoria || <span className="text-[#5C635A] italic">non specificato</span>}</div>
          )}
        </div>
        {FIELDS.map(([k, label]) => (
          <div key={k} className="border-b border-black/5 py-3">
            <div className="label-eyebrow mb-1">{label}</div>
            {editing ? (
              <input
                className="input-base"
                value={draftIdent[k] || ""}
                onChange={(e) => setIdent(k, e.target.value)}
                data-testid={`edit-${k}`}
              />
            ) : (
              <div className="text-[#1E2520]">{ident[k] || <span className="text-[#5C635A] italic">non specificato</span>}</div>
            )}
          </div>
        ))}
        <div className="border-b border-black/5 py-3">
          <div className="label-eyebrow mb-1">Valore stimato (€)</div>
          {editing ? (
            <input
              type="number"
              className="input-base"
              value={draftIdent.valore_stimato_eur ?? ""}
              onChange={(e) => setIdent("valore_stimato_eur", e.target.value === "" ? null : parseFloat(e.target.value))}
              data-testid="edit-valore"
            />
          ) : (
            <div className="text-[#1E2520]">€ {(ident.valore_stimato_eur ?? 0).toLocaleString("it-IT")}</div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="card p-6 md:p-8 mb-8">
        <div className="label-eyebrow mb-3">Descrizione storica</div>
        {editing ? (
          <textarea
            className="input-base min-h-[140px]"
            value={draftIdent.descrizione || ""}
            onChange={(e) => setIdent("descrizione", e.target.value)}
            data-testid="edit-descrizione"
          />
        ) : (
          <p className="text-[#1E2520] leading-relaxed font-serif text-lg">
            {ident.descrizione || <span className="text-[#5C635A] italic">Nessuna descrizione.</span>}
          </p>
        )}
        {ident.note_valore && !editing && (
          <p className="text-sm text-[#5C635A] mt-4 italic">Stima: {ident.note_valore}</p>
        )}
      </div>

      {/* Aneddoto storico */}
      <div className="card p-6 md:p-8 mb-8 relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-6 -right-6 font-serif text-[180px] leading-none text-[#9C5B42]/10 select-none pointer-events-none"
        >
          ❝
        </div>
        <div className="label-eyebrow mb-3 flex items-center gap-2">
          <Sparkle weight="duotone" size={14} className="text-[#9C5B42]" /> Aneddoto storico
        </div>
        {editing ? (
          <textarea
            className="input-base min-h-[120px]"
            value={draftIdent.aneddoto_storico || ""}
            onChange={(e) => setIdent("aneddoto_storico", e.target.value)}
            data-testid="edit-aneddoto"
            placeholder="Una curiosità, un fatto storico, un dettaglio affascinante..."
          />
        ) : (
          <p className="text-[#1E2520] leading-relaxed font-serif text-lg italic relative z-10">
            {ident.aneddoto_storico || <span className="text-[#5C635A] not-italic">L'AI non ha fornito un aneddoto per questo reperto. Puoi aggiungerne uno tu cliccando "Correggi".</span>}
          </p>
        )}
      </div>

      {/* Notes */}
      <div className="card p-6 md:p-8 mb-8">
        <div className="label-eyebrow mb-3">Note personali</div>
        {editing ? (
          <textarea
            className="input-base min-h-[100px]"
            value={draft.user_notes || ""}
            onChange={(e) => setDraft((d) => ({ ...d, user_notes: e.target.value }))}
            data-testid="edit-user-notes"
            placeholder="Luogo del ritrovamento, condizioni, restauro..."
          />
        ) : (
          <p className="text-[#1E2520] whitespace-pre-wrap">
            {item.user_notes || <span className="text-[#5C635A] italic">Nessuna nota.</span>}
          </p>
        )}
      </div>

      {/* Location */}
      <div className="card p-6 md:p-8 mb-8">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="label-eyebrow flex items-center gap-2">
            <MapPin weight="duotone" /> Luogo del ritrovamento
          </div>
          {editing && (
            <button
              type="button"
              onClick={() => setDraft((d) => ({ ...d, location_visible_in_share: !d.location_visible_in_share }))}
              className="text-xs text-[#5C635A] hover:text-[#1E2520] inline-flex items-center gap-1.5"
              data-testid="toggle-location-share"
            >
              {draft.location_visible_in_share ? (
                <><Eye weight="duotone" size={14} /> Visibile nei link ospite</>
              ) : (
                <><EyeSlash weight="duotone" size={14} /> Nascosto nei link ospite</>
              )}
            </button>
          )}
        </div>
        {editing ? (
          <LocationPicker
            value={draft.location}
            onChange={(loc) => setDraft((d) => ({ ...d, location: loc }))}
          />
        ) : item.location?.lat ? (
          <div>
            {item.location.label && (
              <div className="text-[#1E2520] mb-2 font-serif text-lg">{item.location.label}</div>
            )}
            <div className="card overflow-hidden mb-2">
              <WorldMap
                center={[item.location.lat, item.location.lng]}
                zoom={14}
                markers={[{ position: [item.location.lat, item.location.lng], code: item.inventory_code }]}
                height="min(calc(100dvh - 280px), 480px)"
              />
            </div>
            <div className="text-xs text-[#5C635A] font-mono">
              {item.location.lat.toFixed(5)}, {item.location.lng.toFixed(5)}
              {item.location.alt != null && ` · ${Math.round(item.location.alt)} m s.l.m.`}
            </div>
            <div className="text-xs text-[#5C635A] mt-1 flex items-center gap-1.5">
              {item.location_visible_in_share ? (
                <><Eye weight="duotone" size={12} /> Visibile a chi ha il link ospite</>
              ) : (
                <><EyeSlash weight="duotone" size={12} /> Nascosto nei link ospite (solo tu lo vedi)</>
              )}
            </div>
          </div>
        ) : (
          <p className="text-[#5C635A] italic text-sm">
            Nessuna posizione registrata. Apri "Correggi" per aggiungerla.
          </p>
        )}
      </div>

      {/* Re-identify */}
      <div className="card p-6 md:p-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle weight="duotone" size={20} className="text-[#9C5B42]" />
          <h3 className="font-serif text-2xl text-[#1E2520]">Chiedi all'AI di rianalizzare</h3>
        </div>
        <p className="text-sm text-[#5C635A] mb-4">
          Se l'identificazione non ti convince, fornisci suggerimenti e l'AI rianalizzerà
          le foto tenendo conto delle tue indicazioni.{" "}
          <strong className="text-[#1E2520]">
            Più foto carichi (max {MAX_PHOTOS}), più dettagli ha l'AI.
          </strong>
        </p>
        <textarea
          className="input-base min-h-[100px] mb-4"
          placeholder="Es: in realtà è una moneta medievale, non romana. Sembra del XIII secolo..."
          value={directives}
          onChange={(e) => setDirectives(e.target.value)}
          data-testid="reidentify-directives"
        />

        {/* Quick "+ photo" affordance specific to the reidentify panel —
            uses the same hidden file input + handler as the photo grid. */}
        <div className="flex flex-wrap gap-3 items-center mb-2">
          <button
            onClick={reidentify}
            disabled={reidentifying || !directives.trim()}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="reidentify-btn"
          >
            <Sparkle weight="duotone" size={18} />
            {reidentifying
              ? "Rianalisi in corso..."
              : `Ricalcola con ${(item.photos || []).length} foto`}
          </button>

          {(item.photos || []).length < MAX_PHOTOS && (
            <>
              {(photoInputMode === "file" || photoInputMode === "file_and_camera") && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoBusy || reidentifying}
                  className="btn-ghost inline-flex items-center gap-2"
                  data-testid="reidentify-add-photo-btn"
                >
                  <Plus size={16} />
                  {photoBusy
                    ? "Caricamento..."
                    : `Aggiungi file (${(item.photos || []).length}/${MAX_PHOTOS})`}
                </button>
              )}
              {(photoInputMode === "camera" || photoInputMode === "file_and_camera") && (
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={photoBusy || reidentifying}
                  className="btn-ghost inline-flex items-center gap-2"
                  data-testid="reidentify-camera-btn"
                >
                  <Plus size={16} /> Scatta foto
                </button>
              )}
            </>
          )}

          <span className="text-xs text-[#5C635A]">
            {(item.photos || []).length >= MAX_PHOTOS
              ? "Hai raggiunto il massimo di foto."
              : "Tip: foto del retro o di iscrizioni in primo piano aiutano molto."}
          </span>
        </div>
      </div>

      {/* Save bar — visible while editing, mirrors the top actions so the
          user doesn't have to scroll back up after long edits. */}
      {editing && (
        <div className="mt-8 flex flex-wrap gap-3 justify-end items-center border-t border-[#E5E0D8] pt-6" data-testid="bottom-save-bar">
          <span className="text-xs text-[#5C635A] mr-auto">Hai modifiche non salvate.</span>
          <button
            onClick={() => { setDraft(item); setEditing(false); }}
            className="btn-ghost inline-flex items-center gap-2"
            data-testid="cancel-edit-btn-bottom"
          >
            <X size={18} /> Annulla
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="save-btn-bottom"
          >
            <FloppyDisk weight="duotone" size={18} />
            {saving ? "Salvataggio..." : "Salva modifiche"}
          </button>
        </div>
      )}

      {/* Lightbox — rendered via Portal to escape ancestor `transform` stacking context */}
      {lightbox && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
          style={{ touchAction: "pinch-zoom" }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 bg-black/60 text-white rounded-full p-2 z-10"
            data-testid="lightbox-close"
            aria-label="Chiudi"
          >
            <X size={24} />
          </button>
          <img
            key={lightbox}
            src={fileUrl(lightbox)}
            alt="Foto reperto ingrandita"
            className="block max-h-[90vh] max-w-[96vw] w-auto h-auto object-contain select-none"
            style={{ touchAction: "pinch-zoom" }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body,
      )}

      {/* Share modal — rendered via Portal */}
      {shareOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShareOpen(false)}
          data-testid="share-modal"
        >
          <div
            className="bg-[#F9F8F6] max-w-md w-full p-6 md:p-8 rounded-md border border-black/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-eyebrow mb-1">Condividi</div>
                <h3 className="font-serif text-2xl text-[#1E2520]">Scheda pubblica</h3>
              </div>
              <button onClick={() => setShareOpen(false)} className="text-[#5C635A] hover:text-[#1E2520]">
                <X size={22} />
              </button>
            </div>

            <p className="text-sm text-[#5C635A] mb-5">
              {item.is_public
                ? "Chi ha il link può vedere foto, identificazione e valore stimato (le note personali restano private)."
                : "Attiva la condivisione per generare un link pubblico."}
            </p>

            <div className="flex items-center gap-2 mb-5">
              {item.is_public ? (
                <button
                  onClick={toggleShare}
                  className="btn-ghost inline-flex items-center gap-2 text-sm"
                  data-testid="share-disable"
                >
                  <GlobeX weight="duotone" size={18} /> Disattiva
                </button>
              ) : (
                <button
                  onClick={toggleShare}
                  className="btn-primary inline-flex items-center gap-2 text-sm"
                  data-testid="share-enable"
                >
                  <Globe weight="duotone" size={18} /> Attiva condivisione
                </button>
              )}
            </div>

            {item.is_public && item.share_token && (
              <>
                <div className="card p-3 flex items-center gap-2 mb-4">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 bg-transparent border-0 text-sm text-[#1E2520] outline-none"
                    data-testid="share-link"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={copyLink}
                    className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1"
                    data-testid="share-copy"
                  >
                    <Copy size={14} /> {copied ? "Copiato!" : "Copia"}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button onClick={nativeShare} className="btn-primary text-sm inline-flex items-center gap-2 flex-1 justify-center" data-testid="share-native">
                    <ShareNetwork weight="duotone" size={16} /> Condividi
                  </button>
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost text-sm inline-flex items-center gap-2"
                  >
                    Anteprima
                  </a>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
